<template>
  <div class="cg-container" id="pdf-content">
    <!-- Header -->
    <div class="cg-header d-flex justify-space-between align-center px-6 position-relative">
      <!-- Left: Empty space to balance flexbox -->
      <div class="d-flex align-center" style="flex: 1;">
      </div>
      <!-- Center: Logo -->
      <div class="d-flex justify-center align-center" style="flex: 1;">
        <img src="/wtilogo.png" alt="WTI Logo" style="max-height: 45px;" />
      </div>
      <!-- Right: Download Button -->
      <div class="d-flex justify-end align-center" style="flex: 1;">
        <v-btn outlined dark @click="downloadPDF" :loading="isPrinting" class="text-none" :small="$vuetify.breakpoint.smAndDown">
          <v-icon left :small="$vuetify.breakpoint.smAndDown">mdi-download</v-icon> <span class="d-none d-sm-inline">Download</span><span class="d-inline d-sm-none" style="font-size: 12px;">Download</span>
        </v-btn>
      </div>
    </div>

    <div v-if="loading" class="d-flex justify-center align-center" style="height: 100%;">
      <v-progress-circular indeterminate color="#005f32"></v-progress-circular>
    </div>

    <div v-else class="cg-content pa-4">
      
      <!-- Row 1: Info, Quals, Actions -->
      <v-row class="row-1 mb-2" dense>
        <v-col cols="12" md="4">
          <v-card class="fill-height rounded-lg d-flex flex-column pa-2 cg-card text-left" outlined>
            <div class="font-weight-bold mb-1" style="font-size: 14px;">Employee Info</div>
            <div class="d-flex align-start">
              <v-avatar size="80" rounded class="mr-4 grey lighten-4">
                <img v-if="empData.photo" :src="empData.photo.startsWith('http') ? empData.photo : mediaURL + empData.photo" alt="Photo" style="object-fit: cover; width: 100%; height: 100%;" @error="empData.photo = null">
                <v-icon v-else size="70" color="grey">mdi-account-circle</v-icon>
              </v-avatar>
              <div class="text-left mt-0">
                <div class="text-subtitle-1 font-weight-bold text-uppercase mb-0" style="line-height: 1.1;">{{ empData.name || 'UNKNOWN' }}</div>
                <div class="text-caption" style="line-height: 1.2;"><strong>Emp ID:</strong> {{ empData.employeeId || 'N/A' }}</div>
                <div class="text-caption" style="line-height: 1.2;"><strong>Date Of Joining:</strong> {{ formatDate(empData.dateofjoining) }}</div>
                <div class="text-caption" style="line-height: 1.2;"><strong>Tenure:</strong> {{ calculateTenure(empData.dateofjoining) }}</div>
                <div class="text-caption" style="line-height: 1.2;"><strong>Exp before WTI:</strong> {{ formatExperienceBeforeWTI(empData.experienceBeforeWTI) }}</div>
                <div class="text-caption" style="line-height: 1.2;"><strong>Total Experience:</strong> {{ calculateTotalExperience(empData.dateofjoining, empData.experienceBeforeWTI) }}</div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="fill-height rounded-lg pa-2 cg-card d-flex flex-column text-left" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-bold" style="font-size: 14px;">Qualifications & Skills</div>
              <v-btn v-if="isAdmin" icon x-small color="#005f32" @click="openDialog('qualification')"><v-icon>mdi-plus</v-icon></v-btn>
            </div>
            <div class="flex-grow-1 overflow-y-auto" style="min-height: 0;">
                <ul class="text-caption pl-4 mb-0 text-left" style="color: #333; line-height: 1.2; word-break: break-word;">
                <li v-for="(qual, i) in graphData.qualifications" :key="'q'+i" class="mb-1 text-left">
                    {{ qual.description }}
                </li>
                <li v-if="!graphData.qualifications || graphData.qualifications.length === 0" class="grey--text list-style-none ml-n4">
                    No qualifications added.
                </li>
                </ul>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="fill-height rounded-lg pa-2 cg-card d-flex flex-column" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
                <div class="font-weight-bold" style="font-size: 14px;">Disciplinary Actions</div>
                <v-btn v-if="isAdmin" icon x-small color="#005f32" @click="openDialog('disciplinary')"><v-icon>mdi-plus</v-icon></v-btn>
            </div>
            <div class="flex-grow-1 overflow-y-auto" style="min-height: 0;">
                <ul class="text-caption pl-4 mb-0 text-left" style="color: #333; line-height: 1.2; word-break: break-word;">
                <li v-for="(action, i) in graphData.disciplinaryActions" :key="'da'+i" class="mb-1 text-left" style="color: red;">
                    <span style="color: #333;">{{ action.description }}</span>
                </li>
                <li v-if="!graphData.disciplinaryActions || graphData.disciplinaryActions.length === 0" class="grey--text list-style-none ml-n4">
                    No disciplinary actions on record.
                </li>
                </ul>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Row 2: Timeline -->
      <v-row class="row-2 mb-2" no-gutters>
        <v-col cols="12">
          <v-card class="fill-height rounded-lg pt-1 px-2 pb-2 cg-card d-flex flex-column overflow-hidden" outlined>
            <div class="d-flex justify-space-between align-center mb-0 mt-0">
               <div class="font-weight-bold mx-auto text-subtitle-2">Key Event Timeline</div>
               <v-btn v-if="isAdmin" icon x-small color="#005f32" class="position-absolute" style="right: 16px;" @click="openDialog('timeline')"><v-icon>mdi-plus</v-icon></v-btn>
            </div>
            
            <div class="timeline-wrapper flex-grow-1 position-relative mx-2">
               <div class="position-relative d-flex align-center" style="width: 100%; height: 100%; padding: 0 80px;">
                   <div class="position-relative" style="width: 100%;">
                       <div class="timeline-line" style="left: 0; right: 0; width: 100%;"></div>
                       <div v-for="(event, i) in sortedTimeline" :key="'t'+i" class="timeline-node text-center" :style="{ left: sortedTimeline.length === 1 ? '50%' : `${(i/(sortedTimeline.length-1))*100}%` }">
                         <div class="timeline-title-top d-flex align-center justify-center mb-1">
                           <v-icon v-if="event.title.toLowerCase().includes('join')" small color="#005f32" class="mr-1">mdi-account-check</v-icon>
                           <v-icon v-else-if="event.title.toLowerCase().includes('promot')" small color="#005f32" class="mr-1">mdi-trending-up</v-icon>
                           <v-icon v-else-if="event.title.toLowerCase().includes('warn')" small color="red" class="mr-1">mdi-alert</v-icon>
                           <v-icon v-else small color="#005f32" class="mr-1">mdi-star-circle</v-icon>
                           <span class="text-caption font-weight-bold text-uppercase" style="font-size: 11px !important; white-space: nowrap;">[{{ event.title }}]</span>
                         </div>
                         <div class="timeline-dot" :class="{'bg-red': event.title.toLowerCase().includes('warn')}"></div>
                         <div class="timeline-title-bottom mt-1">
                           <div class="text-caption" style="line-height: 1;">{{ event.yearCount || event.financialYear }}</div>
                           <div class="text-caption grey--text mt-1" style="font-size: 10px !important; line-height: 1;">({{ formatFY(event.financialYear) || formatDate(event.date) }})</div>
                         </div>
                       </div>
                   </div>
               </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Row 3 Options -->
      <div class="d-flex justify-end align-center mb-1 print-hide custom-small-toggles">
         <v-checkbox v-model="showCTCTrend" label="Show CTC Trend" hide-details class="mt-0 pt-0 mr-4" dense :ripple="false"></v-checkbox>
         <v-switch v-model="singleViewMode" label="Single View Mode" hide-details class="mt-0 pt-0" dense :ripple="false"></v-switch>
      </div>

      <!-- Row 3: Charts -->
      <v-row class="row-3 mb-2" dense v-if="!singleViewMode">
        <!-- CTC -->
        <v-col cols="12" :md="showCTCTrend ? 4 : 0" v-if="showCTCTrend">
          <v-card class="fill-height rounded-lg pt-1 px-2 pb-1 cg-card d-flex flex-column overflow-hidden" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-bold mx-auto text-subtitle-2">CTC Trend (K)</div>
              <v-btn v-if="isAdmin" icon small color="#005f32" class="position-absolute" style="right: 16px;" @click="openDialog('graphData', 'ctc')"><v-icon>mdi-plus</v-icon></v-btn>
            </div>
            <div class="flex-grow-1 chart-container mt-1">
              <apexchart :key="'ctc-' + showCTCTrend + '-' + singleViewMode" type="area" height="100%" width="100%" :options="ctcChartOptions" :series="ctcSeries"></apexchart>
            </div>
          </v-card>
        </v-col>
        <!-- Grade -->
        <v-col cols="12" :md="showCTCTrend ? 4 : 6">
          <v-card class="fill-height rounded-lg pt-1 px-2 pb-1 cg-card d-flex flex-column overflow-hidden" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-bold mx-auto text-subtitle-2">Grade Progression</div>
              <v-btn v-if="isAdmin" icon small color="#005f32" class="position-absolute" style="right: 16px;" @click="openDialog('graphData', 'grade')"><v-icon>mdi-plus</v-icon></v-btn>
            </div>
            <div class="flex-grow-1 chart-container mt-1">
              <apexchart :key="'grade-' + showCTCTrend + '-' + singleViewMode" type="line" height="100%" width="100%" :options="gradeChartOptions" :series="gradeSeries"></apexchart>
            </div>
          </v-card>
        </v-col>
        <!-- PMR -->
        <v-col cols="12" :md="showCTCTrend ? 4 : 6">
          <v-card class="fill-height rounded-lg pt-1 px-2 pb-1 cg-card d-flex flex-column overflow-hidden" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-bold mx-auto text-subtitle-2">PMR Rating</div>
              <v-btn v-if="isAdmin" icon small color="#005f32" class="position-absolute" style="right: 16px;" @click="openDialog('graphData', 'pmr')"><v-icon>mdi-plus</v-icon></v-btn>
            </div>
            <div class="flex-grow-1 chart-container mt-1">
              <apexchart :key="'pmr-' + showCTCTrend + '-' + singleViewMode" type="bar" height="100%" width="100%" :options="pmrChartOptions" :series="pmrSeries"></apexchart>
            </div>
          </v-card>
        </v-col>
      </v-row>
      
      <v-row class="row-3 mb-2" dense v-else>
         <v-col cols="12">
            <v-card class="fill-height rounded-lg pt-1 px-2 pb-1 cg-card d-flex flex-column overflow-hidden position-relative" outlined>
                <div class="d-flex justify-space-between align-center mb-1">
                    <div class="font-weight-bold mx-auto text-subtitle-2">Combined Chart</div>
                </div>

                <div class="flex-grow-1 chart-container mt-1">
                    <apexchart :key="'combined-' + showCTCTrend + '-' + singleViewMode" type="line" height="100%" width="100%" :options="combinedChartOptions" :series="combinedSeries"></apexchart>
                </div>
            </v-card>
         </v-col>
      </v-row>
      <!-- Universal Grade Legend (Visible only in single view, keeps space to prevent layout jump) -->
      <div class="d-flex justify-start align-center mt-1" :style="{ color: '#555', fontSize: '11px', visibility: singleViewMode ? 'visible' : 'hidden' }">
          <strong>Grade Legend:</strong>&nbsp; I=7 &nbsp;|&nbsp; H=6.5 &nbsp;|&nbsp; G=6 &nbsp;|&nbsp; F=5.5 &nbsp;|&nbsp; E4=5 &nbsp;|&nbsp; E3=4.5 &nbsp;|&nbsp; E2=4 &nbsp;|&nbsp; E1=3.5 &nbsp;|&nbsp; D=3 &nbsp;|&nbsp; C2=2.5 &nbsp;|&nbsp; C1=2 &nbsp;|&nbsp; B=1.5 &nbsp;|&nbsp; A=1
      </div>

    </div>

    <!-- Dialogs -->
    <v-dialog v-model="dialogs.qualification" max-width="500px">
        <v-card>
            <v-card-title>Add Qualification</v-card-title>
            <v-card-text>
                <v-text-field v-model="forms.qualification.description" label="Description" outlined dense></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.qualification = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="submitQualification" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.disciplinary" max-width="500px">
        <v-card>
            <v-card-title>Add Disciplinary Action</v-card-title>
            <v-card-text>
                <v-text-field v-model="forms.disciplinary.description" label="Description" outlined dense></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.disciplinary = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="submitDisciplinary" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.timeline" max-width="500px">
        <v-card>
            <v-card-title>Add Timeline Event</v-card-title>
            <v-card-text>
                <v-text-field v-model="forms.timeline.title" label="Title (e.g. Promoted to AM)" outlined dense></v-text-field>
                <v-text-field v-model="forms.timeline.financialYear" label="Financial Year (e.g. 2023-2024)" outlined dense></v-text-field>
                <v-text-field v-model="forms.timeline.yearCount" label="Year Count (e.g. Year 5)" outlined dense></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.timeline = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="submitTimeline" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.graphData" max-width="400px">
        <v-card>
            <v-card-title>
                Add {{ graphDataType === 'ctc' ? 'CTC Data' : (graphDataType === 'grade' ? 'Grade Progression' : 'PMR Rating') }}
            </v-card-title>
            <v-card-text>
                <v-text-field v-model="forms.graphData.financialYear" label="Financial Year (e.g. 2023-2024)" outlined dense class="mb-3"></v-text-field>
                <v-text-field v-model="forms.graphData.yearCount" label="Year Count (e.g. Year 5)" outlined dense class="mb-3"></v-text-field>
                <v-text-field v-if="graphDataType === 'ctc'" v-model="forms.graphData.ctc" label="CTC Amount" type="number" outlined dense hide-details></v-text-field>
                <v-text-field v-if="graphDataType === 'grade'" v-model="forms.graphData.grade" label="Grade (e.g. F or 4.5)" outlined dense hide-details></v-text-field>
                <v-text-field v-if="graphDataType === 'pmr'" v-model="forms.graphData.pmr" label="PMR Rating" type="number" outlined dense hide-details></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.graphData = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="submitGraphData" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

  </div>
</template>

<script>
import axios from 'axios';
import VueApexCharts from 'vue-apexcharts';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

export default {
  name: 'CareerGraphView',
  components: {
    apexchart: VueApexCharts
  },
  data() {
    return { 
      graphData: {},
      loading: true,
      saving: false,
      isPrinting: false,
      showCTCTrend: true,
      singleViewMode: false,
      baseDocUrl: this.baseURL ? this.baseURL + 'u/' : 'http://localhost:5000/u/',
      graphDataType: 'ctc',
      dialogs: {
          qualification: false,
          disciplinary: false,
          timeline: false,
          graphData: false
      },
      forms: {
          qualification: { description: '' },
          disciplinary: { description: '' },
          timeline: { title: '', financialYear: '', yearCount: '' },
          graphData: { financialYear: '', yearCount: '', ctc: null, grade: '', pmr: null }
      }
    };
  },
  computed: {
    isAdmin() {
        return this.$store.getters.isAdmin;
    },
    empData() {
        return this.graphData.employeeId || {};
    },
    sortedTimeline() {
        if(!this.graphData.timeline) return [];
        return [...this.graphData.timeline].sort((a,b) => new Date(a.date || a.createdAt) - new Date(b.date || b.createdAt));
    },
    chartCategories() {
        let cats = new Set();
        (this.graphData.ctcTrend || []).forEach(d => cats.add(d.yearCount));
        (this.graphData.gradeProgression || []).forEach(d => cats.add(d.yearCount));
        (this.graphData.pmrTrend || []).forEach(d => cats.add(d.yearCount));
        let arr = Array.from(cats);
        arr.sort((a, b) => {
            let numA = parseInt(a.replace(/[^\d]/g, '')) || 0;
            let numB = parseInt(b.replace(/[^\d]/g, '')) || 0;
            return numA - numB;
        });
        return arr;
    },
    
    // CTC
    ctcCategories() {
        let arr = (this.graphData.ctcTrend || []).map(d => d.yearCount);
        return [...new Set(arr)].sort((a,b) => (parseInt(a.replace(/[^\d]/g, ''))||0) - (parseInt(b.replace(/[^\d]/g, ''))||0));
    },
    ctcSeries() {
        const data = this.ctcCategories.map(cat => this.getValForCategory('ctc', cat) || null);
        return [{ name: 'CTC', data: data }];
    },
    ctcChartOptions() {
        const ctcData = this.ctcSeries[0].data.filter(d => d !== null);
        let minCtc = undefined;
        if (ctcData.length > 0) {
            // Start from initial salary rounded down to nearest 10k (e.g., 32k -> 30k)
            let initial = ctcData[0];
            minCtc = Math.floor(initial / 10000) * 10000;
        }
        return {
            chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: !this.isPrinting } },
            colors: ['#005f32'],
            dataLabels: { 
                enabled: this.isPrinting,
                formatter: (val) => val ? (val/1000).toFixed(1) + 'k' : '',
                style: { fontSize: '10px' }
            },
            stroke: { curve: 'straight', width: 2 },
            fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.0, stops: [0, 100] } },
            xaxis: { 
                categories: this.ctcCategories.map(cat => [
                    cat.replace('Year ', ''),
                    this.formatFY(this.getFYForCategory(cat))
                ]),
                axisBorder: { show: false }, 
                axisTicks: { show: false },
                labels: { style: { fontSize: '10px' } },
                tooltip: { enabled: false }
            },
            yaxis: { 
                min: minCtc,
                labels: { formatter: (val) => val ? (val/1000).toFixed(1) + 'k' : '', style: { fontSize: '10px' } },
                axisBorder: { show: false }
            },
            grid: { show: false, padding: { top: -20, bottom: 0 } }
        };
    },

    // Grade
    gradeCategories() {
        let arr = (this.graphData.gradeProgression || []).map(d => d.yearCount);
        return [...new Set(arr)].sort((a,b) => (parseInt(a.replace(/[^\d]/g, ''))||0) - (parseInt(b.replace(/[^\d]/g, ''))||0));
    },
    gradeSeries() {
        const gradeMap = { 'A': 1, 'B': 1.5, 'C1': 2, 'C2': 2.5, 'D': 3, 'E1': 3.5, 'E2': 4, 'E3': 4.5, 'E4': 5, 'F': 5.5, 'G': 6, 'H': 6.5, 'I': 7 };
        const data = this.gradeCategories.map(cat => {
            let g = this.getValForCategory('grade', cat);
            if (!g) return null;
            g = String(g).trim().toUpperCase();
            if (gradeMap[g]) return gradeMap[g];
            let parsed = parseFloat(g);
            return isNaN(parsed) ? null : parsed;
        });
        return [{ name: 'Grade', data: data }];
    },
    gradeChartOptions() {
        const gradeData = this.gradeSeries[0].data.filter(d => d !== null);
        let minGrade = undefined;
        let maxGrade = undefined;
        let tickAmt = undefined;
        if (gradeData.length > 0) {
            minGrade = gradeData[0]; // exact initial grade value
            maxGrade = 7; // Always set highest grade to 'I' (numeric value 7)
            if (minGrade === maxGrade) {
                minGrade -= 0.5; // Ensure chart renders if min == max
            }
            tickAmt = Math.round((maxGrade - minGrade) / 0.5);
        }

        return {
            chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: !this.isPrinting } },
            colors: ['#005f32'],
            dataLabels: { 
                enabled: this.isPrinting,
                formatter: (val) => {
                    const rMap = { 1: 'A', 1.5: 'B', 2: 'C1', 2.5: 'C2', 3: 'D', 3.5: 'E1', 4: 'E2', 4.5: 'E3', 5: 'E4', 5.5: 'F', 6: 'G', 6.5: 'H', 7: 'I' };
                    let rounded = Math.round(val * 10) / 10;
                    return rMap[rounded] || rounded.toFixed(1);
                },
                style: { fontSize: '10px' },
                background: { enabled: true, foreColor: '#ffffff', color: '#005f32', borderRadius: 2, padding: 4 }
            },
            stroke: { curve: 'straight', width: 2 },
            markers: { size: 0, hover: { size: 4 }, colors: ['#005f32'], strokeColors: '#fff', strokeWidth: 1 },
            xaxis: { 
                categories: this.gradeCategories.map(cat => [
                    cat.replace('Year ', ''),
                    this.formatFY(this.getFYForCategory(cat))
                ]),
                axisBorder: { show: false }, 
                axisTicks: { show: false },
                labels: { style: { fontSize: '10px' } },
                tooltip: { enabled: false }
            },
            yaxis: { 
                min: minGrade,
                max: maxGrade,
                tickAmount: tickAmt,
                labels: { 
                    formatter: (val) => {
                        const rMap = { 1: 'A', 1.5: 'B', 2: 'C1', 2.5: 'C2', 3: 'D', 3.5: 'E1', 4: 'E2', 4.5: 'E3', 5: 'E4', 5.5: 'F', 6: 'G', 6.5: 'H', 7: 'I' };
                        let rounded = Math.round(val * 10) / 10;
                        return rMap[rounded] || rounded.toFixed(1);
                    }, 
                    style: { fontSize: '10px' } 
                },
                axisBorder: { show: false }
            },
            grid: { show: false, padding: { top: -20, bottom: 0 } }
        };
    },

    // PMR
    pmrCategories() {
        let arr = (this.graphData.pmrTrend || []).map(d => d.yearCount);
        return [...new Set(arr)].sort((a,b) => (parseInt(a.replace(/[^\d]/g, ''))||0) - (parseInt(b.replace(/[^\d]/g, ''))||0));
    },
    pmrSeries() {
        const data = this.pmrCategories.map(cat => this.getValForCategory('pmr', cat) || null);
        return [{ name: 'PMR Rating', data: data }];
    },
    pmrChartOptions() {
        return {
            chart: { type: 'bar', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: !this.isPrinting } },
            colors: ['#005f32'],
            dataLabels: { 
                enabled: this.isPrinting,
                formatter: (val) => val ? val.toFixed(1) : '',
                style: { fontSize: '10px' }
            },
            plotOptions: { bar: { borderRadius: 0, columnWidth: '40%' } },
            xaxis: { 
                categories: this.pmrCategories.map(cat => [
                    cat.replace('Year ', ''),
                    this.formatFY(this.getFYForCategory(cat))
                ]),
                axisBorder: { show: false }, 
                axisTicks: { show: false },
                labels: { style: { fontSize: '10px' } },
                tooltip: { enabled: false }
            },
            yaxis: { 
                min: 2.0,
                max: 10.0,
                tickAmount: 8,
                labels: { formatter: (val) => val ? val.toFixed(1) : '', style: { fontSize: '10px' } },
                axisBorder: { show: false }
            },
            grid: { show: false, padding: { top: -20, bottom: 0 } }
        };
    },

    combinedSeries() {
        let series = [];
        const ctcData = this.chartCategories.map(cat => this.getValForCategory('ctc', cat) || null);
        const pmrData = this.chartCategories.map(cat => this.getValForCategory('pmr', cat) || null);
        const gradeMap = { 'A': 1, 'B': 1.5, 'C1': 2, 'C2': 2.5, 'D': 3, 'E1': 3.5, 'E2': 4, 'E3': 4.5, 'E4': 5, 'F': 5.5, 'G': 6, 'H': 6.5, 'I': 7 };
        const gradeData = this.chartCategories.map(cat => {
            let g = this.getValForCategory('grade', cat);
            if (!g) return null;
            g = String(g).trim().toUpperCase();
            if (gradeMap[g]) return gradeMap[g];
            let parsed = parseFloat(g);
            return isNaN(parsed) ? null : parsed;
        });

        if (this.showCTCTrend) {
            series.push({ name: 'CTC', type: 'line', data: ctcData });
        }
        series.push({ name: 'PMR Rating', type: 'column', data: pmrData });
        series.push({ name: 'Grade (Numeric)', type: 'line', data: gradeData });
        return series;
    },
    combinedChartOptions() {
        let yaxisConfig = [];
        let colorsConfig = [];
        let strokeWidths = [];

        if (this.showCTCTrend) {
            colorsConfig.push('#008FFB');
            strokeWidths.push(3);
            yaxisConfig.push({ 
                seriesName: 'CTC', 
                title: { text: 'CTC', style: { fontWeight: 'bold' } },
                labels: { formatter: (val) => val ? val.toFixed(1) : '' },
                axisBorder: { show: false }
            });
        }

        colorsConfig.push('#00E396', '#FEB019');
        strokeWidths.push(0, 3);
        
        yaxisConfig.push({ 
            seriesName: 'PMR Rating', 
            opposite: this.showCTCTrend, 
            title: { text: 'PMR / Grade', style: { fontWeight: 'bold' } }, 
            min: 0, 
            max: 10,
            labels: { formatter: (val) => val ? val.toFixed(1) : '' },
            axisBorder: { show: false }
        });
        
        yaxisConfig.push({ 
            seriesName: 'Grade (Numeric)', 
            opposite: this.showCTCTrend, 
            show: false, 
            min: 0, 
            max: 10 
        });

        return {
            chart: { stacked: false, toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: !this.isPrinting } },
            stroke: { width: strokeWidths, curve: 'smooth' },
            colors: colorsConfig,
            dataLabels: { 
                enabled: false
            },
            xaxis: { 
                categories: this.chartCategories.map(cat => [cat.replace('Year ', ''), this.formatFY(this.getFYForCategory(cat))]),
                axisBorder: { show: false },
                axisTicks: { show: false }
            },
            yaxis: yaxisConfig,
            legend: { position: 'bottom', markers: { radius: 12 } },
            grid: { show: true, borderColor: '#e0e0e0', strokeDashArray: 0, padding: { top: -20, bottom: 0 } }
        };
    }
  },
  async created() {
    await this.fetchGraphData();
  },
  methods: {
    async fetchGraphData() {
      try {
        const employeeId = this.$route.params.employeeId;
        const token = localStorage.getItem('token');
        const res = await axios.get('/career-graph/' + employeeId, { 
          headers: { 'x-auth-token': token } 
        });
        if (res.data.status) {
          this.graphData = res.data.data;
        }
      } catch (e) {
        console.error("Error fetching graph data:", e);
      } finally {
        this.loading = false;
      }
    },
    openDialog(type, subType) {
        if (type === 'graphData') {
            this.graphDataType = subType;
            
            this.forms.graphData.yearCount = '';
            this.forms.graphData.financialYear = '';
            this.forms.graphData.ctc = null;
            this.forms.graphData.grade = '';
            this.forms.graphData.pmr = null;
        }
        this.dialogs[type] = true;
    },
    async submitQualification() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const payload = {
                employeeId,
                qualification: {
                    title: 'Educational Qualification',
                    description: this.forms.qualification.description,
                    date: new Date()
                }
            };
            await axios.post('/career-graph/qualification', payload, { headers: { 'x-auth-token': token } });
            this.dialogs.qualification = false;
            this.forms.qualification.description = '';
            await this.fetchGraphData();
        } catch(e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async submitDisciplinary() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const payload = {
                employeeId,
                action: {
                    description: this.forms.disciplinary.description,
                    date: new Date()
                }
            };
            await axios.post('/career-graph/disciplinary-action', payload, { headers: { 'x-auth-token': token } });
            this.dialogs.disciplinary = false;
            this.forms.disciplinary = { description: '' };
            await this.fetchGraphData();
        } catch(e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async submitTimeline() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            
            let formattedYearCount = (this.forms.timeline.yearCount || '').trim();
            if (/^\d+$/.test(formattedYearCount)) {
                formattedYearCount = 'Year ' + formattedYearCount;
            }

            const payload = {
                employeeId,
                event: {
                    title: this.forms.timeline.title,
                    financialYear: this.forms.timeline.financialYear,
                    yearCount: formattedYearCount,
                    date: new Date()
                }
            };
            await axios.post('/career-graph/event', payload, { headers: { 'x-auth-token': token } });
            this.dialogs.timeline = false;
            this.forms.timeline = { title: '', financialYear: '', yearCount: '' };
            await this.fetchGraphData();
        } catch(e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async submitGraphData() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            
            let formattedYearCount = (this.forms.graphData.yearCount || '').trim();
            if (/^\d+$/.test(formattedYearCount)) {
                formattedYearCount = 'Year ' + formattedYearCount;
            }

            const payload = {
                employeeId,
                financialYear: this.forms.graphData.financialYear,
                yearCount: formattedYearCount,
                ctc: this.forms.graphData.ctc,
                grade: this.forms.graphData.grade,
                pmr: this.forms.graphData.pmr
            };
            await axios.post('/career-graph/graph-data', payload, { headers: { 'x-auth-token': token } });
            this.dialogs.graphData = false;
            this.forms.graphData = { financialYear: '', yearCount: '', ctc: null, grade: '', pmr: null };
            await this.fetchGraphData();
        } catch(e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    formatDate(dateStr) {
        if(!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    formatFY(fyStr) {
        if(!fyStr) return '';
        const parts = fyStr.split('-');
        if(parts.length === 2) {
            return parts[0].slice(-2) + '-' + parts[1].slice(-2);
        }
        return fyStr;
    },
    calculateTenure(dojStr) {
        if(!dojStr) return 'N/A';
        const doj = new Date(dojStr);
        const now = new Date();
        const diffYears = (now - doj) / (1000 * 60 * 60 * 24 * 365.25);
        return diffYears.toFixed(1) + ' Years';
    },
    formatExperienceBeforeWTI(expBefore) {
        if (!expBefore) return '0 Years';
        let str = String(expBefore).trim();
        if (str.toLowerCase().includes('year') || str.toLowerCase().includes('yr')) return str;
        return str + ' Years';
    },
    calculateTotalExperience(dojStr, expBefore) {
        let tenureYears = 0;
        if(dojStr) {
            const doj = new Date(dojStr);
            const now = new Date();
            tenureYears = (now - doj) / (1000 * 60 * 60 * 24 * 365.25);
        }
        
        let beforeYears = 0;
        if(expBefore) {
            let match = String(expBefore).match(/[\d.]+/);
            if(match && !isNaN(parseFloat(match[0]))) {
                beforeYears = parseFloat(match[0]);
            }
        }
        
        if (tenureYears === 0 && beforeYears === 0) return 'N/A';
        return (tenureYears + beforeYears).toFixed(1) + ' Years';
    },
    getFYForCategory(cat) {
        let entry = (this.graphData.ctcTrend || []).find(d => d.yearCount === cat) ||
                    (this.graphData.gradeProgression || []).find(d => d.yearCount === cat) ||
                    (this.graphData.pmrTrend || []).find(d => d.yearCount === cat);
        return entry ? entry.financialYear : '';
    },
    getValForCategory(type, cat) {
        let entry;
        if(type === 'ctc') entry = (this.graphData.ctcTrend || []).find(d => d.yearCount === cat);
        if(type === 'grade') entry = (this.graphData.gradeProgression || []).find(d => d.yearCount === cat);
        if(type === 'pmr') entry = (this.graphData.pmrTrend || []).find(d => d.yearCount === cat);
        
        if(!entry) return null;
        if(type === 'ctc') return entry.amount;
        if(type === 'grade') return entry.grade;
        if(type === 'pmr') return entry.rating;
    },
    async downloadPDF() {
        this.isPrinting = true;
        await this.$nextTick();
        
        // Allow ApexCharts time to re-render SVG with data labels
        setTimeout(async () => {
            const element = document.querySelector('.cg-container');
            if (element) {
                try {
                    const canvas = await html2canvas(element, { 
                        scale: 2, 
                        useCORS: true,
                        logging: false,
                        backgroundColor: '#f5f5f5',
                        ignoreElements: (node) => {
                            return node.classList && (node.classList.contains('print-hide') || node.classList.contains('v-btn'));
                        }
                    });
                    
                    const imgData = canvas.toDataURL('image/png');
                    
                    // Initialize A4 Landscape PDF
                    const pdf = new jsPDF('l', 'mm', 'a4');
                    const pdfWidth = pdf.internal.pageSize.getWidth();
                    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
                    
                    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
                    pdf.save('Career_Graph.pdf');
                } catch(err) {
                    console.error("Error generating PDF", err);
                    alert("Error downloading PDF: " + (err.message || err));
                } finally {
                    this.isPrinting = false;
                }
            } else {
                this.isPrinting = false;
            }
        }, 600);
    }
  }
};
</script>

<style scoped>
/* Main Layout to prevent scroll */
.cg-container {
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  overflow: hidden; 
}
.cg-header {
  height: 60px;
  background-color: #004d40;
  flex-shrink: 0;
}
.cg-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Row Heights - using flexbox to fill available space optimally */
.row-1 { flex: 0 0 auto; height: auto; min-height: 0; }
.row-2 { flex: 0 0 auto; height: 110px; min-height: 0; }
.row-3 { flex: 1 1 0; min-height: 250px; }

/* Cards */
.cg-card {
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important;
  border-color: #e0e0e0 !important;
}

/* Timeline specific styling */
.timeline-wrapper {
  margin-top: 5px;
}
.timeline-line {
  position: absolute;
  left: 0;
  right: 0;
  top: 40%;
  height: 3px;
  background-color: #005f32;
  transform: translateY(-50%);
  z-index: 0;
}
.timeline-node {
  position: absolute; 
  width: 140px;
  margin-left: -70px;
  top: 40%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
}
.timeline-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: #005f32;
  border: 2px solid #fff;
  z-index: 2;
  position: relative;
}
.bg-red {
  background-color: #d32f2f !important;
}
.timeline-title-top {
  position: absolute;
  bottom: 14px;
  width: 200px;
  left: 50%;
  transform: translateX(-50%);
}
.timeline-title-bottom {
  position: absolute;
  top: 14px;
  width: 120px;
  left: 50%;
  transform: translateX(-50%);
}

/* Toggles */
.custom-small-toggles {
  transform: scale(0.85);
  transform-origin: right center;
}
.custom-small-toggles .v-input--selection-controls__input::before {
  display: none !important;
}

/* Chart container must not overflow */
.chart-container {
  min-height: 0;
  position: relative;
}
.chart-container > div {
  height: 100% !important;
}

.w-100 { width: 100%; }
.position-relative { position: relative; }

/* Responsive adjustments for mobile and tablets */
@media (max-width: 960px) {
  .cg-container {
    height: auto !important;
    min-height: calc(100vh - 64px);
    overflow: visible !important;
  }
  .cg-content {
    overflow: visible !important;
    display: block !important;
  }
  .row-1, .row-3 {
    flex: none !important;
    height: auto !important;
  }
  .row-1 .cg-card {
    min-height: 200px !important;
    height: auto !important;
  }
  .row-2 {
    flex: none !important;
    height: 140px !important; 
  }
  .chart-container {
    height: 300px !important;
  }
  .timeline-wrapper {
    overflow-x: auto !important;
    overflow-y: hidden !important;
    -webkit-overflow-scrolling: touch;
  }
  .timeline-wrapper > div {
    min-width: 800px !important;
  }
}

.position-absolute { position: absolute; }
.list-style-none { list-style: none; }

/* Print adjustments */
@media print {
  body * { visibility: hidden; }
  .cg-container, .cg-container * { visibility: visible; }
  .cg-container { 
    position: absolute !important; 
    left: 0 !important; 
    top: 0 !important; 
    width: 100vw !important;
    height: auto !important; 
    overflow: visible !important; 
  }
  .cg-header, .cg-header * { display: none !important; visibility: hidden !important; }
  .print-hide, .print-hide * { display: none !important; visibility: hidden !important; }
  
  .row-1, .row-2, .row-3 { height: auto !important; page-break-inside: avoid; }
  .cg-card { margin-bottom: 20px; box-shadow: none !important; border: 1px solid #000 !important; }
  .chart-container { height: 300px !important; }
  /* hide toggles in print */
  .d-flex.justify-end.align-center.mb-2 { display: none !important; }
}
</style>
